import { ScoreParseError } from './errors';
import type {
  ClefInfo,
  ClefKind,
  EndingMark,
  Lyric,
  MeasureInfo,
  ParsedPart,
  ParsedScore,
  RawNote,
  RawRest,
  SummaryWarning,
  TempoMark,
} from './model';
import { assignLines } from './lines';
import { child, childNumber, childText, childrenNamed, hasChild, parseXml, type XmlNode } from './xml';

const STEP_SEMITONES: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

const BEAT_UNIT_QUARTERS: Record<string, number> = {
  breve: 8,
  whole: 4,
  half: 2,
  quarter: 1,
  eighth: 0.5,
  '16th': 0.25,
  '32nd': 0.125,
};

const DEFAULT_TIME = { beats: 4, beatType: 4 };

interface PartListEntry {
  name: string;
  abbreviation: string;
  instrumentNames: string[];
}

interface Transposition {
  chromatic: number;
  octaveChange: number;
}

/** Per-measure facts collected from every part, merged into MeasureInfo afterwards. */
interface MeasureAccumulator {
  number: string;
  implicit: boolean;
  time: { beats: number; beatType: number } | null;
  contentLength: number;
  forwardRepeat: boolean;
  forwardRepeatAfterJump: boolean;
  backwardRepeat: { times: number; afterJump?: boolean } | null;
  endingStart: EndingMark | null;
  endingStop: EndingMark | null;
  segno: boolean;
  coda: boolean;
  toCoda: boolean;
  daCapo: boolean;
  dalSegno: boolean;
  jumpWithRepeats: boolean;
  fine: boolean;
  rehearsalMarks: string[];
}

function emptyAccumulator(number: string, implicit: boolean): MeasureAccumulator {
  return {
    number,
    implicit,
    time: null,
    contentLength: 0,
    forwardRepeat: false,
    forwardRepeatAfterJump: false,
    backwardRepeat: null,
    endingStart: null,
    endingStop: null,
    segno: false,
    coda: false,
    toCoda: false,
    daCapo: false,
    dalSegno: false,
    jumpWithRepeats: false,
    fine: false,
    rehearsalMarks: [],
  };
}

function roundBeat(x: number): number {
  return Math.round(x * 1e9) / 1e9;
}

function firstNumber(text: string | null): number | null {
  if (!text) return null;
  const m = /-?\d+(\.\d+)?/.exec(text);
  return m ? Number(m[0]) : null;
}

export function clefKind(sign: string, line: number | null, octaveChange: number): ClefKind {
  const s = sign.toUpperCase();
  if (s === 'G' && (line === null || line === 2)) {
    if (octaveChange === -1) return 'treble8vb';
    if (octaveChange === 1) return 'treble8va';
    return octaveChange === 0 ? 'treble' : 'other';
  }
  if (s === 'F' && (line === null || line === 4)) {
    if (octaveChange === -1) return 'bass8vb';
    return octaveChange === 0 ? 'bass' : 'other';
  }
  if (s === 'C' && line === 3) return 'alto';
  if (s === 'C' && line === 4) return 'tenor';
  if (s === 'PERCUSSION') return 'percussion';
  return 'other';
}

function parseEndingNumbers(raw: string | undefined): number[] {
  const numbers = (raw ?? '')
    .split(/[\s,]+/)
    .map((t) => Number.parseInt(t, 10))
    .filter((n) => Number.isFinite(n) && n > 0);
  return numbers.length > 0 ? [...new Set(numbers)].sort((a, b) => a - b) : [1];
}

function readPartList(root: XmlNode): Map<string, PartListEntry> {
  const entries = new Map<string, PartListEntry>();
  for (const scorePart of childrenNamed(child(root, 'part-list'), 'score-part')) {
    const id = scorePart.attrs['id'];
    if (!id) continue;
    entries.set(id, {
      name: childText(scorePart, 'part-name') ?? '',
      abbreviation: childText(scorePart, 'part-abbreviation') ?? '',
      instrumentNames: childrenNamed(scorePart, 'score-instrument')
        .map((si) => childText(si, 'instrument-name') ?? '')
        .filter((n) => n !== ''),
    });
  }
  return entries;
}

function readMetadata(root: XmlNode): Pick<ParsedScore, 'title' | 'subtitle' | 'composer' | 'lyricist' | 'arranger'> {
  const work = child(root, 'work');
  const creators = childrenNamed(child(root, 'identification'), 'creator');
  const creator = (type: string): string | null =>
    creators.find((c) => c.attrs['type'] === type && c.text !== '')?.text ?? null;

  const credits = childrenNamed(root, 'credit');
  const credit = (type: string): string | null => {
    for (const c of credits) {
      if (childText(c, 'credit-type') === type) {
        const words = childrenNamed(c, 'credit-words')
          .map((w) => w.text)
          .join(' ')
          .trim();
        if (words) return words;
      }
    }
    return null;
  };

  const title = childText(work, 'work-title') || childText(root, 'movement-title') || credit('title');
  return {
    title: title || null,
    subtitle: credit('subtitle'),
    composer: creator('composer') ?? credit('composer'),
    lyricist: creator('lyricist') ?? creator('poet') ?? credit('lyricist'),
    arranger: creator('arranger') ?? credit('arranger'),
  };
}

function readLyrics(note: XmlNode): Lyric[] {
  const lyrics: Lyric[] = [];
  for (const lyric of childrenNamed(note, 'lyric')) {
    const texts = childrenNamed(lyric, 'text').map((t) => t.text);
    const text = texts.join(' ').trim();
    if (!text) continue;
    const verse = Number.parseInt(lyric.attrs['number'] ?? '1', 10);
    const syllabicRaw = childText(lyric, 'syllabic');
    const syllabic =
      syllabicRaw === 'begin' || syllabicRaw === 'middle' || syllabicRaw === 'end' ? syllabicRaw : 'single';
    lyrics.push({ verse: Number.isFinite(verse) && verse > 0 ? verse : 1, text, syllabic });
  }
  return lyrics;
}

/** "con rep.", "con ripetizione" or "with repeats" in a jump's text: repeats are played after the jump. */
const WITH_REPEATS = /\bcon\s*rip|\bcon\s*rep|\bwith\s+repeats?\b/i;

/** Apply `<sound>` attributes (tempo and jumps) to the measure. Returns the tempo, if any. */
function readSound(sound: XmlNode | undefined, acc: MeasureAccumulator): number | null {
  if (!sound) return null;
  const a = sound.attrs;
  if (a['segno'] !== undefined) acc.segno = true;
  if (a['coda'] !== undefined) acc.coda = true;
  if (a['tocoda'] !== undefined) acc.toCoda = true;
  if (a['dacapo'] === 'yes') acc.daCapo = true;
  if (a['dalsegno'] !== undefined) acc.dalSegno = true;
  if (a['fine'] !== undefined) acc.fine = true;
  const tempo = a['tempo'] !== undefined ? Number(a['tempo']) : NaN;
  return Number.isFinite(tempo) && tempo > 0 ? tempo : null;
}

interface PartReadResult {
  part: ParsedPart;
  tempoMarks: TempoMark[];
}

function readPart(
  partNode: XmlNode,
  entry: PartListEntry | undefined,
  accumulators: MeasureAccumulator[],
): PartReadResult {
  const id = partNode.attrs['id'] ?? '';
  const notes: RawNote[] = [];
  const rests: RawRest[] = [];
  const tempoMarks: TempoMark[] = [];
  const clefs: ClefInfo[] = [];
  const voicesByStaff = new Map<number, Set<string>>();
  const transposeByStaff = new Map<number, Transposition>();
  let divisions = 1;
  let staffCount = 1;
  let graceNoteCount = 0;
  let hasTranspose = false;

  const measures = childrenNamed(partNode, 'measure');
  measures.forEach((measure, measureIndex) => {
    let acc = accumulators[measureIndex];
    if (!acc) {
      acc = emptyAccumulator(measure.attrs['number'] ?? String(measureIndex + 1), measure.attrs['implicit'] === 'yes');
      accumulators[measureIndex] = acc;
    }
    let pos = 0;
    let maxPos = 0;
    let lastNoteStart = 0;

    for (const el of measure.children) {
      switch (el.name) {
        case 'attributes': {
          const div = childNumber(el, 'divisions');
          if (div !== null && div > 0) divisions = div;
          const staves = childNumber(el, 'staves');
          if (staves !== null && staves > staffCount) staffCount = staves;
          const time = child(el, 'time');
          if (time && !hasChild(time, 'senza-misura') && acc.time === null) {
            const beats = (childText(time, 'beats') ?? '')
              .split('+')
              .map((b) => Number(b))
              .reduce((s, b) => s + (Number.isFinite(b) ? b : 0), 0);
            const beatType = childNumber(time, 'beat-type');
            if (beats > 0 && beatType !== null && beatType > 0) acc.time = { beats, beatType };
          }
          for (const clef of childrenNamed(el, 'clef')) {
            const staff = Number.parseInt(clef.attrs['number'] ?? '1', 10) || 1;
            const sign = childText(clef, 'sign') ?? '';
            const line = childNumber(clef, 'line');
            const octaveChange = childNumber(clef, 'clef-octave-change') ?? 0;
            const kind = clefKind(sign, line, octaveChange);
            if (!clefs.some((c) => c.staff === staff && c.kind === kind && c.sign === sign && c.line === line)) {
              clefs.push({ staff, sign, line, octaveChange, kind });
            }
          }
          for (const transpose of childrenNamed(el, 'transpose')) {
            const staff = Number.parseInt(transpose.attrs['number'] ?? '0', 10) || 0;
            const t = {
              chromatic: childNumber(transpose, 'chromatic') ?? 0,
              octaveChange: childNumber(transpose, 'octave-change') ?? 0,
            };
            if (t.chromatic !== 0 || t.octaveChange !== 0) hasTranspose = true;
            transposeByStaff.set(staff, t);
          }
          break;
        }
        case 'backup': {
          pos = Math.max(0, pos - (childNumber(el, 'duration') ?? 0) / divisions);
          break;
        }
        case 'forward': {
          pos += (childNumber(el, 'duration') ?? 0) / divisions;
          maxPos = Math.max(maxPos, pos);
          break;
        }
        case 'note': {
          if (hasChild(el, 'grace')) {
            graceNoteCount++;
            break;
          }
          const duration = (childNumber(el, 'duration') ?? 0) / divisions;
          const isChord = hasChild(el, 'chord');
          const start = isChord ? lastNoteStart : pos;
          if (!isChord) {
            lastNoteStart = pos;
            pos += duration;
            maxPos = Math.max(maxPos, pos);
          }
          if (hasChild(el, 'rest') && !isChord && !hasChild(el, 'cue')) {
            rests.push({
              measureIndex,
              offset: roundBeat(start),
              duration: roundBeat(duration),
              staff: childNumber(el, 'staff') ?? 1,
              voice: childText(el, 'voice') ?? '1',
              visible: el.attrs['print-object'] !== 'no',
            });
            break;
          }
          const pitch = child(el, 'pitch');
          if (!pitch || hasChild(el, 'rest') || hasChild(el, 'cue')) break;

          const step = (childText(pitch, 'step') ?? 'C').toUpperCase();
          const octave = childNumber(pitch, 'octave') ?? 4;
          const alter = childNumber(pitch, 'alter') ?? 0;
          const staff = childNumber(el, 'staff') ?? 1;
          if (staff > staffCount) staffCount = staff;
          const voice = childText(el, 'voice') ?? '1';
          // MusicXML <pitch> in an octave clef (treble-8 tenor) is already at sounding pitch:
          // the clef-octave-change only affects how the note is drawn. Only <transpose> applies.
          const t = transposeByStaff.get(staff) ?? transposeByStaff.get(0) ?? { chromatic: 0, octaveChange: 0 };
          const midi = (octave + 1) * 12 + (STEP_SEMITONES[step] ?? 0) + alter + t.chromatic + 12 * t.octaveChange;

          const ties = childrenNamed(el, 'tie').map((tie) => tie.attrs['type']);
          let voices = voicesByStaff.get(staff);
          if (!voices) {
            voices = new Set();
            voicesByStaff.set(staff, voices);
          }
          voices.add(voice);
          notes.push({
            measureIndex,
            offset: roundBeat(start),
            duration: roundBeat(duration),
            midi,
            spelling: { step, alter, octave },
            staff,
            voice,
            tieStart: ties.includes('start'),
            tieStop: ties.includes('stop'),
            lyrics: readLyrics(el),
            line: 0,
          });
          break;
        }
        case 'direction': {
          const offsetDivs = childNumber(el, 'offset') ?? 0;
          const at = Math.max(0, pos + offsetDivs / divisions);
          let soundTempo = readSound(child(el, 'sound'), acc);
          let metronome: { beatUnit: string; dotted: boolean; perMinute: number } | null = null;
          for (const directionType of childrenNamed(el, 'direction-type')) {
            for (const rehearsal of childrenNamed(directionType, 'rehearsal')) {
              if (rehearsal.text && !acc.rehearsalMarks.includes(rehearsal.text)) acc.rehearsalMarks.push(rehearsal.text);
            }
            // "D.S. con rep.": MusicXML exports from MuseScore lose a jump's "play repeats" setting,
            // so the text written with the jump says it.
            for (const words of childrenNamed(directionType, 'words')) {
              if (words.text && WITH_REPEATS.test(words.text)) acc.jumpWithRepeats = true;
            }
            const met = child(directionType, 'metronome');
            const beatUnit = childText(met, 'beat-unit');
            const perMinute = firstNumber(childText(met, 'per-minute'));
            if (met && beatUnit && perMinute !== null && perMinute > 0) {
              metronome = { beatUnit, dotted: hasChild(met, 'beat-unit-dot'), perMinute };
            }
          }
          if (soundTempo === null && metronome) {
            const unit = BEAT_UNIT_QUARTERS[metronome.beatUnit] ?? 1;
            soundTempo = metronome.perMinute * unit * (metronome.dotted ? 1.5 : 1);
          }
          if (soundTempo !== null) {
            tempoMarks.push({
              measureIndex,
              offset: roundBeat(at),
              quarterBpm: soundTempo,
              beatUnit: metronome?.beatUnit ?? null,
              dotted: metronome?.dotted ?? false,
              perMinute: metronome?.perMinute ?? null,
            });
          }
          break;
        }
        case 'sound': {
          const tempo = readSound(el, acc);
          if (tempo !== null) {
            tempoMarks.push({
              measureIndex,
              offset: roundBeat(pos),
              quarterBpm: tempo,
              beatUnit: null,
              dotted: false,
              perMinute: null,
            });
          }
          break;
        }
        case 'barline': {
          const repeat = child(el, 'repeat');
          const afterJump = repeat?.attrs['after-jump'] === 'yes';
          if (repeat?.attrs['direction'] === 'forward') {
            acc.forwardRepeat = true;
            if (afterJump) acc.forwardRepeatAfterJump = true;
          }
          if (repeat?.attrs['direction'] === 'backward' && !acc.backwardRepeat) {
            const times = Number.parseInt(repeat.attrs['times'] ?? '2', 10);
            acc.backwardRepeat = { times: Number.isFinite(times) && times >= 2 ? times : 2, ...(afterJump ? { afterJump } : {}) };
          }
          const ending = child(el, 'ending');
          const type = ending?.attrs['type'];
          if (ending && (type === 'start' || type === 'stop' || type === 'discontinue')) {
            const mark: EndingMark = { numbers: parseEndingNumbers(ending.attrs['number']), type };
            if (type === 'start' && !acc.endingStart) acc.endingStart = mark;
            if (type !== 'start' && !acc.endingStop) acc.endingStop = mark;
          }
          readSound(child(el, 'sound'), acc);
          break;
        }
        default:
          break;
      }
    }
    acc.contentLength = Math.max(acc.contentLength, roundBeat(maxPos));
  });

  assignLines(notes, rests);
  let maxVoicesPerStaff = 0;
  for (const voices of voicesByStaff.values()) maxVoicesPerStaff = Math.max(maxVoicesPerStaff, voices.size);

  return {
    part: {
      id,
      name: entry?.name ?? '',
      abbreviation: entry?.abbreviation ?? '',
      instrumentNames: entry?.instrumentNames ?? [],
      staffCount,
      clefs,
      notes,
      rests,
      maxVoicesPerStaff,
      graceNoteCount,
      hasTranspose,
    },
    tempoMarks,
  };
}

/** Mark which measures belong to an ending (volta), and the highest number in each ending group. */
function assignEndings(measures: MeasureInfo[]): void {
  let open: number[] | null = null;
  for (const m of measures) {
    if (m.endingStart) open = m.endingStart.numbers;
    if (open) m.ending = { numbers: open, groupMax: 0 };
    if (m.endingStop) {
      if (!m.ending) m.ending = { numbers: m.endingStop.numbers, groupMax: 0 };
      open = null;
    }
  }
  // A group is a run of consecutive measures that belong to endings.
  let i = 0;
  while (i < measures.length) {
    if (!measures[i]?.ending) {
      i++;
      continue;
    }
    let j = i;
    let max = 0;
    while (j < measures.length && measures[j]?.ending) {
      max = Math.max(max, ...(measures[j]?.ending?.numbers ?? [1]));
      j++;
    }
    for (let k = i; k < j; k++) {
      const ending = measures[k]?.ending;
      if (ending) ending.groupMax = max;
    }
    i = j;
  }
}

/** Read MusicXML text (partwise) into a ParsedScore. */
export function readScore(xml: string): ParsedScore {
  const tree = parseXml(xml);
  const root = tree.find((n) => n.name === 'score-partwise' || n.name === 'score-timewise');
  if (!root) {
    throw new ScoreParseError('notMusicXml', 'The file is XML but not a MusicXML score');
  }
  if (root.name === 'score-timewise') {
    throw new ScoreParseError('timewiseNotSupported', 'Timewise MusicXML is not supported; export partwise');
  }
  const partList = readPartList(root);
  const partNodes = childrenNamed(root, 'part');
  if (partNodes.length === 0) throw new ScoreParseError('noParts', 'The score has no parts');

  const readWarnings: SummaryWarning[] = [];
  const accumulators: MeasureAccumulator[] = [];
  const parts: ParsedPart[] = [];
  const tempoByPosition = new Map<string, TempoMark>();
  const measureCounts = new Set<number>();

  for (const partNode of partNodes) {
    const { part, tempoMarks } = readPart(partNode, partList.get(partNode.attrs['id'] ?? ''), accumulators);
    parts.push(part);
    measureCounts.add(childrenNamed(partNode, 'measure').length);
    for (const mark of tempoMarks) {
      const key = `${mark.measureIndex}:${mark.offset}`;
      if (!tempoByPosition.has(key)) tempoByPosition.set(key, mark);
    }
  }
  if (measureCounts.size > 1) readWarnings.push({ code: 'measureCountMismatch' });
  if (parts.every((p) => p.notes.length === 0)) throw new ScoreParseError('noNotes', 'The score has no notes');

  let time = DEFAULT_TIME;
  const measures: MeasureInfo[] = accumulators.map((acc, index) => {
    const timeSignatureChange = acc.time !== null;
    if (acc.time) time = acc.time;
    const nominal = (time.beats * 4) / time.beatType;
    return {
      index,
      number: acc.number,
      implicit: acc.implicit,
      timeSignature: time,
      timeSignatureChange,
      length: acc.contentLength > 0 ? acc.contentLength : roundBeat(nominal),
      forwardRepeat: acc.forwardRepeat,
      ...(acc.forwardRepeatAfterJump ? { forwardRepeatAfterJump: true } : {}),
      backwardRepeat: acc.backwardRepeat,
      endingStart: acc.endingStart,
      endingStop: acc.endingStop,
      ending: null,
      segno: acc.segno,
      coda: acc.coda,
      toCoda: acc.toCoda,
      daCapo: acc.daCapo,
      dalSegno: acc.dalSegno,
      ...(acc.jumpWithRepeats && (acc.dalSegno || acc.daCapo) ? { jumpWithRepeats: true } : {}),
      fine: acc.fine,
      rehearsalMarks: acc.rehearsalMarks,
    };
  });
  assignEndings(measures);

  const tempoMarks = [...tempoByPosition.values()].sort(
    (a, b) => a.measureIndex - b.measureIndex || a.offset - b.offset,
  );

  return { ...readMetadata(root), parts, measures, tempoMarks, readWarnings };
}
